import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';
import {orderSessionSteps, sessionCompletionPercentage} from "business/session";
import {PAGE_HEIGHT_WITH_NAVBAR} from "common/helpers";
import {history} from 'common/history';
import {color, font, space, type} from "common/tokens";
import {useSetTranslation} from "common/useSetTranslation";
import CtaButton from "components/redesign/CtaButton";
import Skeleton from "components/redesign/Skeleton";
import {filter, findIndex, flatten, get, isUndefined, some} from "lodash";
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import SwipeableViews from "components/SwipeableViews";
import SessionStep from "views/session/SessionStep";

const useStyles = makeStyles({
  page: {
    background: color.SURFACE,
    height: PAGE_HEIGHT_WITH_NAVBAR,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  container: {
    width: '100%',
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    padding: '0 32px',
    boxSizing: 'border-box',
  },
  column: {
    width: 638,
    maxWidth: '100%',
    margin: '0 auto',
  },
  topBar: {
    flexShrink: 0,
    background: color.SURFACE,
    paddingBottom: 18,
  },
  back: {
    ...type.body,
    fontSize: 18,
    lineHeight: '26px',
    fontWeight: font.SEMIBOLD,
    color: color.TEXT,
    appearance: 'none',
    background: 'none',
    border: 'none',
    width: '100%',
    padding: '14px 0 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    textAlign: 'left',
    gap: 10,
    cursor: 'pointer',
  },
  backArrow: {
    color: color.TEXT_MUTED,
    fontSize: 20,
    lineHeight: 1,
    flexShrink: 0,
  },
  progressTrack: {
    height: 4,
    borderRadius: 4,
    background: color.DIVIDER,
    overflow: 'hidden',
  },
  progressFill: {
    display: 'block',
    height: '100%',
    borderRadius: 4,
    background: color.ACCENT,
  },
  scroller: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowAnchor: 'none',
  },
  footer: {
    flexShrink: 0,
    borderTop: `1px solid ${color.DIVIDER}`,
  },
  footerInner: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 0',
  },
  spacer: {
    flex: 1,
  },
  titleSkeleton: {
    padding: '14px 0 16px',
  },
  contentSkeleton: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    paddingTop: 24,
  },
});

const SessionStepWrapper = ({
  location,
  session,
  role,
  initQuiz,
  stepCompleted,
  initQuizAnswers,
  quizAnswers,
  initSession,
  startSession,
  currentLearningHubSessionStatus,
}) => {
  const classes = useStyles();
  const {t} = useTranslation();
  const {st} = useSetTranslation();

  const sessionId = session.id;

  useEffect(() => {
    if (sessionId) startSession(sessionId, true) && initSession(sessionId, true);
  }, [t]);

  const orderedSessionSteps = orderSessionSteps(session);
  const requestedStepId = get(location, 'state.stepId');
  const [currentStep, setCurrentStep] = useState(0);
  const [honouredRequest, setHonouredRequest] = useState(false);

  useEffect(() => {
    if (honouredRequest || !requestedStepId || !orderedSessionSteps.length) return;
    const index = findIndex(orderedSessionSteps, step => step.id === requestedStepId);
    if (index >= 0) setCurrentStep(index);
    setHonouredRequest(true);
  }, [requestedStepId, orderedSessionSteps.length, honouredRequest]);

  const sessionProgress = sessionCompletionPercentage(session);

  const quizHasMandatoryQuestions = quiz =>
    flatten(quiz.questions).some(question => question.mandatory === true);

  const isStepCompleted = (stepIndex, answers, sid) => {
    const step = orderedSessionSteps[stepIndex];
    const stepContent = get(step, 'content');
    const mandatoryQuizContents = filter(
      stepContent,
      content => content.contentType.toLowerCase() === 'quiz' && quizHasMandatoryQuestions(content.quiz)
    );

    const answersForMandatoryQuizContents = mandatoryQuizContents.map(quiz =>
      get(answers, `[${sid}][${step.id}][${quiz.quiz.id}].answers`)
    );

    return !some(answersForMandatoryQuizContents, answer => answer === undefined);
  };

  const handleStepChange = index => {
    const step = Math.min(Math.max(index, 0), orderedSessionSteps.length - 1);
    setCurrentStep(step);
    stepCompleted(session, orderedSessionSteps[step], quizAnswers);
  };

  const nextStepChange = (index, answers, sid) => {
    if (!isStepCompleted(index, answers, sid)) return;
    const next = Math.min(index + 1, orderedSessionSteps.length - 1);
    setCurrentStep(next);
    stepCompleted(session, orderedSessionSteps[index], answers);
  };

  const isFirstRunOfSession = status => !(get(status, 'status') === "COMPLETED");

  const scrollerRef = useRef(null);
  useLayoutEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
  }, [currentStep]);

  if (isUndefined(sessionId) || !orderedSessionSteps.length) {
    return (
      <div className={classes.page}>
        <div className={classes.topBar}>
          <div className={classes.container}>
            <div className={classes.column}>
              <div className={classes.titleSkeleton}>
                <Skeleton width={220} height={26}/>
              </div>
              <div className={classes.progressTrack}/>
            </div>
          </div>
        </div>
        <div className={classes.scroller}>
          <div className={classes.container}>
            <div className={classNames(classes.column, classes.contentSkeleton)}>
              <Skeleton width={'55%'} height={28}/>
              <Skeleton height={220}/>
              <Skeleton height={16}/>
              <Skeleton width={'85%'} height={16}/>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const isLastPage = currentStep === orderedSessionSteps.length - 1;
  const canLeaveStep = isStepCompleted(currentStep, quizAnswers, sessionId);
  const currentStepData = orderedSessionSteps[currentStep];
  const showDone =
    isLastPage &&
    (isFirstRunOfSession(currentLearningHubSessionStatus) || sessionProgress !== 100) &&
    canLeaveStep;
  // Finished before: the last page offers a way out rather than asking for
  // feedback a second time.
  const isSessionAlreadyComplete =
    get(currentLearningHubSessionStatus, 'status') === 'COMPLETED';

  const handleDoneClick = () => {
    stepCompleted(session, currentStepData, canLeaveStep && quizAnswers);
    history.push('/session/step/sessionfeedback/', {sessionId});
  };

  return (
    <div className={classes.page}>
      <div className={classes.topBar}>
        <div className={classes.container}>
          <div className={classes.column}>
            <button type={'button'} className={classes.back} onClick={() => history.goBack()}>
              <span className={classes.backArrow} aria-hidden={true}>{'←'}</span>
              {st(get(currentStepData, 'title'), get(currentStepData, 'localeTitle'))}
            </button>
            <div className={classes.progressTrack}>
              <span
                className={classes.progressFill}
                style={{width: `${((currentStep + 1) * 100) / orderedSessionSteps.length}%`}}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={classes.scroller} ref={scrollerRef}>
        <div className={classes.container}>
          <div className={classes.column}>
            <SwipeableViews
              index={currentStep}
              enableMouseEvents
              disabled={!canLeaveStep}
              onChangeIndex={index => handleStepChange(index)}
              slideStyle={{overflow: 'hidden'}}
            >
              {orderedSessionSteps.map((step, index) => (
                <SessionStep
                  key={step.id}
                  step={step}
                  sessionId={sessionId}
                  role={role}
                  initQuiz={initQuiz}
                  quizAnswers={quizAnswers}
                  isLearningHub={session.isLearningHub}
                  initQuizAnswers={initQuizAnswers}
                  isInView={index === currentStep}
                />
              ))}
            </SwipeableViews>
          </div>
        </div>
      </div>

      <div className={classes.footer}>
        <div className={classes.container}>
          <div className={classNames(classes.column, classes.footerInner)}>
            {currentStep > 0 && (
              <CtaButton
                size={'large'}
                variant={'secondary'}
                label={`← ${t('previous')}`}
                onClick={() => handleStepChange(currentStep - 1)}
              />
            )}
            <span className={classes.spacer}/>
            {showDone ? (
              <CtaButton
                size={'large'}
                variant={'primary'}
                label={t('markAsDone')}
                onClick={handleDoneClick}
              />
            ) : isLastPage ? (
              isSessionAlreadyComplete && (
                <CtaButton
                  size={'large'}
                  variant={'secondary'}
                  label={t('closeSession')}
                  onClick={() => history.push('/learninghub')}
                />
              )
            ) : (
              <CtaButton
                size={'large'}
                variant={'primary'}
                label={t('nextStep')}
                disabled={!canLeaveStep}
                onClick={() => nextStepChange(currentStep, quizAnswers, sessionId)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionStepWrapper;
